const IMAGE_FORMATS = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'heic', 'avif', 'bmp']
const VIDEO_FORMATS = ['mp4', 'mov', 'webm', 'm4v', 'ogv', 'avi', 'mkv']

export interface FileTypeInfo {
  format?: string
  type?: string
  src?: string
}

export function isPdfFile(file: FileTypeInfo): boolean {
  if (file?.format === 'pdf' || file?.type === 'application/pdf') return true
  if (!file?.src) return false
  const path = file.src.split('?')[0]?.toLowerCase() ?? ''
  return path.endsWith('.pdf')
}

export function isImageFile(file: FileTypeInfo): boolean {
  if (file?.format?.startsWith('image') || file?.type?.startsWith('image')) return true
  if (file?.format && IMAGE_FORMATS.includes(file.format.toLowerCase())) return true
  if (!file?.src) return false
  const ext = file.src.split('?')[0]?.split('.').pop()?.toLowerCase() ?? ''
  return IMAGE_FORMATS.includes(ext)
}

export function isVideoFile(file: FileTypeInfo): boolean {
  if (file?.format?.startsWith('video') || file?.type?.startsWith('video')) return true
  if (file?.format && VIDEO_FORMATS.includes(file.format.toLowerCase())) return true
  if (!file?.src) return false
  const ext = file.src.split('?')[0]?.split('.').pop()?.toLowerCase() ?? ''
  return VIDEO_FORMATS.includes(ext)
}

/**
 * Assembles the playable URL for an S3-backed video, which lives at
 * `src/s3PublicKey` (mirrors ResponsiveVideo). Returns undefined when the file
 * isn't an S3 video so callers can fall back to `src`. Single source of truth
 * for the URL shape so the details pane, gallery, and expand-to-tab don't drift.
 * The leading slash guard keeps a stray `/`-prefixed key from producing a
 * double-slash / protocol-relative URL.
 */
export function getS3VideoUrl(file: { src?: string; s3PublicKey?: string }): string | undefined {
  if (!file?.src || !file?.s3PublicKey) return undefined
  return `${file.src}/${file.s3PublicKey.replace(/^\/+/, '')}`
}

/**
 * Derives a poster (first-frame) image URL for a Cloudinary-hosted video.
 * Cloudinary serves a video's first frame as a `.jpg` at the same delivery URL,
 * so swap the `src` extension to `.jpg`. Self-contained (no cloud-name/provider
 * dependency), mirroring `getS3VideoUrl`. Returns undefined for non-Cloudinary
 * files (S3 videos already carry their own poster via ResponsiveVideo), so
 * passing the result straight to a `<video poster={…}>` is a safe no-op there.
 * Used to give iOS/iPadOS WebKit an image frame instead of a black/grey box on a
 * native `<video>` that hasn't started playing (#21623).
 */
export function getCloudinaryVideoPoster(file: { src?: string; cloudinaryPublicId?: string }): string | undefined {
  // Require an actual Cloudinary delivery URL, not just the presence of a
  // cloudinaryPublicId — otherwise a file whose `src` points elsewhere would get
  // a `.jpg` poster on a non-Cloudinary host, producing a broken image request.
  if (!file?.src || !file?.cloudinaryPublicId || !file.src.includes('res.cloudinary.com')) return undefined
  const [path, query] = file.src.split('?')
  const withoutExt = path.replace(/\.[^./]+$/, '')
  return `${withoutExt}.jpg${query ? `?${query}` : ''}`
}

/**
 * Appends the `#t=0.001` media fragment so iOS/iPadOS WebKit seeks to and paints
 * the video's own first frame as the still, instead of a black/grey box, without
 * depending on a derived poster asset being generated yet (#21623). Cloudinary's
 * derived first-frame `.jpg` (getCloudinaryVideoPoster) is generated on demand and
 * isn't ready in the seconds right after upload, so the poster stalls and WebKit
 * falls back to grey; this nudge paints a frame from the bytes it's already loading
 * for playback. Provider-independent (works for Cloudinary and S3). No-op on a
 * falsy url or one that already carries a fragment.
 */
export function withVideoFirstFrame(url?: string): string | undefined {
  if (!url) return undefined
  return url.includes('#') ? url : `${url}#t=0.001`
}
